import React, { useState } from 'react';

// You will need to import this component and render it from your main application's login route.

const API_URL = 'api'; // *** IMPORTANT: REPLACE WITH YOUR FUNCTION'S URL ***
const COOKIE_DOMAIN = '.verbaw.com'; 

/**
 * Defines the shape of the API response from the Firebase Function.
 * This provides type safety when accessing properties like 'token' and 'message'.
 */
interface ApiResponse {
  token?: string;
  message?: string;
}

/**
 * A helper function to set a cookie in the browser.
 * @param name The name of the cookie.
 * @param value The value to store.
 * @param days The number of days until the cookie expires.
 */
const setCookie = (name: string, value: string, days: number): void => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value}; ${expires}; path=/; domain=${COOKIE_DOMAIN}; Secure; SameSite=Lax`;
};

/**
 * A helper function to get a cookie's value from the browser.
 * @param name The name of the cookie to retrieve.
 * @returns The cookie's value, or null if not found.
 */
const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * A reusable component to handle user authentication via a Firebase Function.
 * This component provides a UI for login and calling a protected API endpoint.
 */
const AuthPage: React.FC = () => {
  // Initialize state by reading the token from the cookie
  const [token, setToken] = useState<string | null>(getCookie('session_token'));
  const [message, setMessage] = useState<string>('Click "Login" to get a token.');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Handles the login process by calling the Firebase Function's login endpoint.
   * On success, it stores the JWT token in state and a cookie.
   */
  const handleLogin = async () => {
    setIsLoading(true);
    setMessage('Logging in...');

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data: ApiResponse = await response.json();

      if (response.ok) {
        if (data.token) {
          // Store the token in both state and a browser cookie
          setToken(data.token);
          setCookie('session_token', data.token, 7); // Store for 7 days
          setCookie('auth2_token', data.token, 7); // Store for 7 days
          setMessage('Login successful! Token received and stored in cookie.');
        } else {
          setMessage('Login failed: Token not found in response.');
          setToken(null);
        }
      } else {
        setMessage(`Login failed: ${data.message || response.statusText}`);
        setToken(null);
      }
    } catch (error) {
      console.error('Login error:', error);
      setMessage('Failed to connect to the server.');
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles the protected API call by sending the JWT token from the state.
   * The state is kept in sync with the cookie.
   */
  const handleProtectedCall = async () => {
    if (!token) {
      setMessage('Please log in first to get a token.');
      return;
    }

    setIsLoading(true);
    setMessage('Calling protected endpoint...');

    try {
      const response = await fetch(`${API_URL}/protected`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data: ApiResponse = await response.json();

      if (response.ok) {
        setMessage(`Protected call successful: "${data.message}"`);
      } else {
        setMessage(`Protected call failed: ${data.message || response.statusText}`);
      }
    } catch (error) {
      console.error('Protected call error:', error);
      setMessage('Failed to connect to the server.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles the logout process by clearing the token from state and the cookie.
   */
  const handleLogout = () => {
    setToken(null);
    document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    setMessage('Logged out. Token has been removed.');
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md text-center border border-gray-200 dark:border-gray-700 mx-auto my-12">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
        Firebase Auth Demo
      </h1>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        This client calls a Firebase Function to handle user login and token validation.
      </p>

      <div className="space-y-4">
        <button
          onClick={handleLogin}
          disabled={isLoading || !!token}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
        >
          {isLoading ? 'Loading...' : 'Login'}
        </button>
        
        <button
          onClick={handleProtectedCall}
          disabled={isLoading || !token}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
        >
          Call Protected Endpoint
        </button>

        <button
          onClick={handleLogout}
          disabled={isLoading || !token}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
        >
          Logout
        </button>
      </div>

      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 text-left">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
          Status:
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
          {message}
        </p>
      </div>

      {token && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 text-left">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
            Your JWT Token:
          </h2>
          <p className="text-xs font-mono text-gray-600 dark:text-gray-400 break-all">
            {token}
          </p>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
